import type { LocalAssetReadPort } from '../adapters/local-asset-port';
import { AssetWireError } from '../adapters/local-asset-wire';
import {
  assetCatalogQuery,
  assetCatalogResult,
  assetDetailResult,
  type LocalAssetCatalogResult,
  type LocalAssetDetailResult,
} from './asset-contract';
export interface AssetReadState<T> {
  readonly requestId: number;
  readonly phase:
    'NOT_LOADED' | 'LOADING' | 'RESULT' | 'TRANSPORT_ERROR' | 'WIRE_INVALID' | 'REQUEST_REJECTED';
  readonly result: T | null;
  readonly assetId: string | null;
}
export function initialAssetReadState<T>(): AssetReadState<T> {
  return { requestId: 0, phase: 'NOT_LOADED', result: null, assetId: null };
}
function session<T>(
  read: (id: string | null, signal: AbortSignal) => Promise<T>,
  changed: (state: AssetReadState<T>) => void,
) {
  let state = initialAssetReadState<T>(),
    sequence = 0,
    disposed = false;
  let controller: AbortController | null = null;
  function publish(next: AssetReadState<T>) {
    state = next;
    if (!disposed) changed(next);
  }
  return {
    async load(assetId: string | null = null): Promise<void> {
      if (disposed) return;
      controller?.abort();
      const active = new AbortController();
      controller = active;
      const requestId = ++sequence;
      if (assetId !== null && (assetId.trim() === '' || ['.', '..'].includes(assetId))) {
        publish({ requestId, phase: 'REQUEST_REJECTED', result: null, assetId });
        return;
      }
      publish({ requestId, phase: 'LOADING', result: null, assetId });
      try {
        const result = await read(assetId, active.signal);
        if (!disposed && sequence === requestId && !active.signal.aborted)
          publish({ requestId, phase: 'RESULT', result, assetId });
      } catch (error) {
        if (!disposed && sequence === requestId && !active.signal.aborted)
          publish({
            requestId,
            phase: error instanceof AssetWireError ? 'WIRE_INVALID' : 'TRANSPORT_ERROR',
            result: null,
            assetId,
          });
      }
    },
    cancel() {
      if (disposed) return;
      controller?.abort();
      publish({ requestId: ++sequence, phase: 'NOT_LOADED', result: null, assetId: null });
    },
    dispose() {
      disposed = true;
      ++sequence;
      controller?.abort();
    },
    current() {
      return state;
    },
  };
}
export function createAssetCatalogSession(
  port: LocalAssetReadPort,
  changed: (state: AssetReadState<LocalAssetCatalogResult>) => void,
) {
  return session(async (_id, signal) => {
    const result = await port.readCatalog(assetCatalogQuery(), signal);
    try {
      return assetCatalogResult(result);
    } catch {
      throw new AssetWireError();
    }
  }, changed);
}
export function createAssetDetailSession(
  port: LocalAssetReadPort,
  changed: (state: AssetReadState<LocalAssetDetailResult>) => void,
) {
  return session(async (id, signal) => {
    if (id === null) throw new Error('Detail requires identity');
    const result = await port.readDetail(id, signal);
    try {
      return assetDetailResult(result, id);
    } catch {
      throw new AssetWireError();
    }
  }, changed);
}
